import { ApiError, type Api } from "../../data/api";
import type { Tone } from "../../model/invoice-view";

/**
 * Vertriebssystem der Betreiber-Konsole (Vertrieb-Konsole, Inhaber 02.11.2026, Migration 0460): Leads, Verlauf, Start und
 * Kundenakte. Lesen über RLS (nur Mitarbeiter mit zweitem Faktor), schreiben nur über die Datenbankfunktionen crm_*.
 */

export type Stufe = "neu" | "kontaktiert" | "demo_geplant" | "demo_gehalten" | "testkonto" | "aktiv_im_test" | "gewonnen" | "verloren" | "spaeter";
export type Quelle = "demo_anfrage" | "testkonto" | "enterprise" | "instagram" | "mail" | "empfehlung" | "sonstiges";
export type Art = "notiz" | "anruf" | "mail" | "termin" | "aufgabe" | "system";

export const STUFEN: readonly { key: Stufe; label: string; tone: Tone }[] = [
  { key: "neu", label: "Neu", tone: "warning" },
  { key: "kontaktiert", label: "Kontaktiert", tone: "info" },
  { key: "demo_geplant", label: "Demo geplant", tone: "info" },
  { key: "demo_gehalten", label: "Demo gehalten", tone: "info" },
  { key: "testkonto", label: "Testkonto", tone: "info" },
  { key: "aktiv_im_test", label: "Aktiv im Test", tone: "success" },
  { key: "gewonnen", label: "Gewonnen (zahlt)", tone: "success" },
  { key: "verloren", label: "Verloren", tone: "neutral" },
  { key: "spaeter", label: "Später", tone: "neutral" },
];
/** Spalten der Pipeline von links nach rechts; „Später“ und „Verloren“ stehen darunter */
export const PIPELINE: readonly Stufe[] = ["neu", "kontaktiert", "demo_geplant", "demo_gehalten", "testkonto", "aktiv_im_test", "gewonnen"];
export const NEBEN: readonly Stufe[] = ["spaeter", "verloren"];

export const QUELLEN: readonly { key: Quelle; label: string }[] = [
  { key: "demo_anfrage", label: "Demo-Anfrage" }, { key: "testkonto", label: "Testkonto" }, { key: "enterprise", label: "Enterprise-Anfrage" },
  { key: "instagram", label: "Instagram" }, { key: "mail", label: "Mail" }, { key: "empfehlung", label: "Empfehlung" }, { key: "sonstiges", label: "Sonstiges" },
];
export const ARTEN: readonly { key: Exclude<Art, "system">; label: string }[] = [
  { key: "notiz", label: "Notiz" }, { key: "anruf", label: "Anruf" }, { key: "mail", label: "Mail" }, { key: "termin", label: "Termin" }, { key: "aufgabe", label: "Aufgabe" },
];
const ART_LABEL: Record<Art, string> = { notiz: "Notiz", anruf: "Anruf", mail: "Mail", termin: "Termin", aufgabe: "Aufgabe", system: "System" };

export const stufeLabel = (s: string) => STUFEN.find((x) => x.key === s)?.label ?? s;
export const stufeTon = (s: string): Tone => STUFEN.find((x) => x.key === s)?.tone ?? "neutral";
export const quelleLabel = (s: string) => QUELLEN.find((x) => x.key === s)?.label ?? s;
export const artLabel = (s: string) => ART_LABEL[s as Art] ?? s;

export interface LeadRow {
  id: string; company: string | null; contact_name: string | null; email: string | null; phone: string | null; website: string | null;
  country: string | null; creators: string | null; source: Quelle; source_detail: string | null; stage: Stufe; owner_id: string | null;
  next_step: string | null; next_step_at: string | null; topic: string | null; plan_interest: string | null; lost_reason: string | null;
  org_id: string | null; demo_request_id: string | null; enterprise_request_id: string | null; last_contact_at: string | null;
  stage_changed_at: string; objected_at: string | null; created_at: string; updated_at: string;
}
export const LEAD_COLUMNS = "id, company, contact_name, email, phone, website, country, creators, source, source_detail, stage, owner_id, next_step, next_step_at, topic, plan_interest, lost_reason, org_id, demo_request_id, enterprise_request_id, last_contact_at, stage_changed_at, objected_at, created_at, updated_at";

export interface ActivityRow {
  id: string; lead_id: string; kind: Art; body: string; due_at: string | null; done_at: string | null; done_by: string | null;
  assignee_id: string | null; for_admin: boolean; author_id: string | null; created_at: string;
}
export interface StaffKurz { user_id: string; name: string; email: string; role: "developer" | "admin" | "sales"; active: boolean; me: boolean }

type Reader = Pick<Api, "select" | "read">;
type Writer = Pick<Api, "rpc">;
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
/** Kennung aus einer Funktion mit Rückgabe uuid (PostgREST liefert sie als Text, manchmal in Anführungszeichen) */
const kennung = (r: unknown): string => String(r).replace(/^"|"$/g, "");

export const LEADS_KEY = ["crm_leads", "alle", "betrieb"] as const;
export const STAFF_KEY = ["crm_staff", "betrieb"] as const;

export async function loadLeads(api: Reader): Promise<LeadRow[]> {
  return api.select<LeadRow>("crm_leads", { columns: LEAD_COLUMNS, order: [["updated_at", "desc"]], limit: 2000 });
}
export async function loadLead(api: Reader, id: string): Promise<{ lead: LeadRow | null; verlauf: ActivityRow[] }> {
  const [leads, verlauf] = await Promise.all([
    api.select<LeadRow>("crm_leads", { columns: LEAD_COLUMNS, filters: [["id", "eq", id]] }),
    api.select<ActivityRow>("crm_activities", { filters: [["lead_id", "eq", id]], order: [["created_at", "desc"]], limit: 300 }),
  ]);
  return { lead: leads[0] ?? null, verlauf };
}
export async function loadStaff(api: Reader): Promise<StaffKurz[]> {
  return parse<StaffKurz[] | null>(await api.read("crm_staff", {})) ?? [];
}

// ── Schreiben ──────────────────────────────────────────────────────────────────────────────────────
export interface LeadInput {
  company?: string; contact_name?: string; email?: string; phone?: string; website?: string; country?: string; creators?: string;
  source?: Quelle; source_detail?: string; topic?: string; plan_interest?: string; next_step?: string; next_step_at?: string;
}
const EMAIL = /^[^@\s"<>]+@[^@\s"<>]+\.[^@\s"<>]{2,}$/;
/** Eingaben vor dem Senden: Leerzeichen weg, leere Felder als leer, Adresse klein und geprüft */
export function leadEingabe(input: LeadInput): LeadInput {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(input)) out[k] = typeof v === "string" ? v.trim() : "";
  if (out.email) {
    out.email = out.email.toLowerCase();
    if (!EMAIL.test(out.email)) throw new ApiError("Bitte eine gültige E-Mail-Adresse angeben.");
  }
  if (out.country) out.country = out.country.toUpperCase();
  return out as LeadInput;
}
export async function createLead(api: Writer, input: LeadInput): Promise<string> {
  const e = leadEingabe(input);
  if (!e.company && !e.contact_name && !e.email) throw new ApiError("Bitte mindestens Firma, Kontaktperson oder E-Mail angeben.");
  return kennung(await api.rpc("crm_lead_create", { p: e }));
}
export async function updateLead(api: Writer, id: string, input: LeadInput): Promise<void> {
  await api.rpc("crm_lead_update", { p_lead: id, p: leadEingabe(input) });
}
export async function setStage(api: Writer, id: string, stage: Stufe, reason: string | null = null): Promise<void> {
  if (stage === "verloren" && (reason ?? "").trim().length < 3) throw new ApiError("Bitte den Grund angeben, warum der Lead verloren ist.");
  await api.rpc("crm_lead_set_stage", { p_lead: id, p_stage: stage, p_reason: reason?.trim() || null });
}
export async function setOwner(api: Writer, id: string, owner: string): Promise<void> {
  await api.rpc("crm_lead_set_owner", { p_lead: id, p_owner: owner });
}
export async function addActivity(api: Writer, id: string, kind: Exclude<Art, "system">, body: string, due: string | null = null, assignee: string | null = null): Promise<string> {
  const text = body.trim();
  if (!text) throw new ApiError("Bitte einen Text eingeben.");
  return kennung(await api.rpc("crm_activity_add", { p_lead: id, p_kind: kind, p_body: text, p_due: kind === "aufgabe" ? due || null : null, p_assignee: kind === "aufgabe" ? assignee : null }));
}
export async function taskDone(api: Writer, activityId: string, done = true): Promise<void> {
  await api.rpc("crm_task_done", { p_activity: activityId, p_done: done });
}
export async function deleteActivity(api: Writer, activityId: string): Promise<void> {
  await api.rpc("crm_activity_delete", { p_activity: activityId });
}
export async function proposeDiscount(api: Writer, id: string, percent: number, months: number | null, reason: string): Promise<string> {
  if (!Number.isInteger(percent) || percent < 1 || percent > 100) throw new ApiError("Rabatt von 1 bis 100 Prozent.");
  if (months !== null && (!Number.isInteger(months) || months < 1 || months > 36)) throw new ApiError("Laufzeit 1 bis 36 Monate (leer = einmalig).");
  if (reason.trim().length < 10) throw new ApiError("Bitte kurz begründen (mindestens 10 Zeichen).");
  return kennung(await api.rpc("crm_discount_propose", { p_lead: id, p_percent: percent, p_months: months, p_reason: reason.trim() }));
}
export async function objectLead(api: Writer, id: string): Promise<void> {
  await api.rpc("crm_lead_object", { p_lead: id });
}
export async function deleteLead(api: Writer, id: string): Promise<void> {
  await api.rpc("crm_lead_delete", { p_lead: id });
}
export async function leadForOrg(api: Writer, orgId: string): Promise<string> {
  return kennung(await api.rpc("crm_lead_for_org", { p_org: orgId }));
}
export async function leadFromDemoRequest(api: Writer, requestId: string): Promise<string> {
  return kennung(await api.rpc("crm_lead_from_demo_request", { p_request: requestId }));
}

// ── Start und Kundenakte ────────────────────────────────────────────────────────────────────────────
export interface StartDaten {
  heute: string;
  faellig: { id: string; lead_id: string; body: string; due_at: string | null; company: string | null; mine: boolean }[];
  naechste_schritte: { lead_id: string; company: string | null; next_step: string | null; next_step_at: string; mine: boolean }[];
  ohne_zustaendigen: { id: string; company: string | null; stage: Stufe; stage_label: string; source: Quelle; created_at: string }[];
  aktiv_im_test: { id: string; company: string | null; org_id: string | null; since: string; trial_ends_at: string | null }[];
  stufen: Partial<Record<Stufe, number>>;
  meine: number;
  fuer_admin: { id: string; lead_id: string; body: string; created_at: string; von: string | null }[];
}
export const START_KEY = ["crm_start", "betrieb"] as const;
export async function loadStart(api: Reader): Promise<StartDaten> {
  const r = parse<StartDaten>(await api.read("crm_start", {}));
  return { ...r, faellig: r.faellig ?? [], naechste_schritte: r.naechste_schritte ?? [], ohne_zustaendigen: r.ohne_zustaendigen ?? [],
    aktiv_im_test: r.aktiv_im_test ?? [], stufen: r.stufen ?? {}, fuer_admin: r.fuer_admin ?? [] };
}

export interface KundenakteDaten {
  lead_id: string | null;
  kontakt: { admins: { name: string | null; email: string; owner: boolean }[]; legal_name: string | null; country: string | null; phone: string | null; website: string | null } | null;
  herkunft: string | null;
  signale: {
    status: string; trial_ends_at: string | null; letzte_anmeldung: string | null;
    aktivierung: { creators: number; deals: number; activated: boolean; vertraege_versendet: number; rechnungen: number; instagram: boolean };
    tickets_offen: number; zahlungsproblem: boolean | null; zahlungsart: boolean | null;
    kuendigung: { am: string; wirksam: string | null; grund: string | null } | null;
  } | null;
}
export async function loadKundenakte(api: Reader, orgId: string): Promise<KundenakteDaten> {
  return parse<KundenakteDaten>(await api.read("crm_account", { p_org: orgId }));
}

// ── Filter und Hilfen (rein, getestet) ──────────────────────────────────────────────────────────────
export interface LeadFilter { suche: string; zustaendig: "alle" | "meine" | "ohne" | string; stufe: "alle" | "offen" | Stufe; quelle: "alle" | Quelle; faellig: "alle" | "faellig" | "ohne_schritt" }
export const ALLE_LEADS: LeadFilter = { suche: "", zustaendig: "alle", stufe: "offen", quelle: "alle", faellig: "alle" };

/** Nächster Schritt: überfällig (vor heute), heute, später oder ohne Datum */
export function faelligkeit(datum: string | null, heute: string): "ueberfaellig" | "heute" | "spaeter" | null {
  if (!datum) return null;
  const d = datum.slice(0, 10);
  return d < heute ? "ueberfaellig" : d === heute ? "heute" : "spaeter";
}
const glatt = (s: string) => s.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss");

export function filterLeads(rows: readonly LeadRow[], f: LeadFilter, me: string | null, heute: string, aktive: ReadonlySet<string> = new Set()): LeadRow[] {
  const q = glatt(f.suche.trim());
  return rows.filter((l) => {
    if (q && !glatt(`${l.company ?? ""} ${l.contact_name ?? ""} ${l.email ?? ""} ${l.topic ?? ""}`).includes(q)) return false;
    if (f.zustaendig === "meine" && l.owner_id !== me) return false;
    if (f.zustaendig === "ohne" && l.owner_id && aktive.has(l.owner_id)) return false;
    if (f.zustaendig === "ohne" && l.owner_id && aktive.size === 0) return false;
    if (!["alle", "meine", "ohne"].includes(f.zustaendig) && l.owner_id !== f.zustaendig) return false;
    if (f.stufe === "offen" && (l.stage === "gewonnen" || l.stage === "verloren")) return false;
    if (f.stufe !== "alle" && f.stufe !== "offen" && l.stage !== f.stufe) return false;
    if (f.quelle !== "alle" && l.source !== f.quelle) return false;
    if (f.faellig === "faellig") {
      const x = faelligkeit(l.next_step_at, heute);
      if (x !== "ueberfaellig" && x !== "heute") return false;
    }
    if (f.faellig === "ohne_schritt" && (l.next_step_at || l.stage === "gewonnen" || l.stage === "verloren")) return false;
    return true;
  });
}

/** Leads je Spalte der Pipeline; in jeder Spalte zuerst fällige, dann zuletzt geändert */
export function spalten(rows: readonly LeadRow[], heute: string): Record<Stufe, LeadRow[]> {
  const out = Object.fromEntries(STUFEN.map((s) => [s.key, [] as LeadRow[]])) as Record<Stufe, LeadRow[]>;
  for (const l of rows) out[l.stage]?.push(l);
  const rang = (l: LeadRow) => ({ ueberfaellig: 0, heute: 1, spaeter: 2 } as const)[faelligkeit(l.next_step_at, heute) ?? "spaeter"] ?? 3;
  for (const k of Object.keys(out) as Stufe[]) out[k].sort((a, b) => rang(a) - rang(b) || b.updated_at.localeCompare(a.updated_at));
  return out;
}

export const leadName = (l: Pick<LeadRow, "company" | "contact_name" | "email">) => l.company ?? l.contact_name ?? l.email ?? "Lead";
export const kuerzel = (name: string) => name.split(/[\s.@]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
export function heuteBerlin(now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}
